import { useLocalization } from "@fluent/react";
import { createElement, useCallback, useMemo, type ReactNode } from "react";
import { modules } from "byzantine";
import type { WebSection } from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences";

import createDragAnnouncements from "../../../../src/components/dragAndDrop/createDragAnnouncements";
import useDragAnnouncementLabels from "../../../../src/components/dragAndDrop/useDragAnnouncementLabels";
import DashboardSectionRow from "../components/DashboardSectionRow";
import SECTION_LABELS_TO_FLUENT_IDS from "../constants";
import DashboardSectionRowContainer from "../components/DashboardSectionRowContainer";

const useDashboardCustomizationRows = () => {
  const { l10n } = useLocalization();
  const dragLabels = useDragAnnouncementLabels();
  const tiles = modules.narmiTiles.useTiles();

  const tileNamesById = useMemo(
    () => new Map(tiles.map((tile) => [tile.client_id, tile.name])),
    [tiles]
  );

  const getItemLabel = useCallback(
    (id: string) => {
      const tileName = tileNamesById.get(id);
      if (tileName) {
        return tileName;
      }
      const ftlId = SECTION_LABELS_TO_FLUENT_IDS[id];
      if (!ftlId) {
        return dragLabels.itemFallback;
      }
      return l10n.getString(ftlId, null) || dragLabels.itemFallback;
    },
    [l10n, dragLabels, tileNamesById]
  );

  const announcements = useMemo(
    () => createDragAnnouncements(dragLabels, getItemLabel),
    [dragLabels, getItemLabel]
  );

  const renderHalfRow = useCallback(
    (
      id: string,
      isSingleChildHalfRow: boolean,
      children: ReactNode,
      sectionIds: string[]
    ) =>
      createElement(DashboardSectionRowContainer, {
        key: id,
        id,
        isSingleChildHalfRow,
        children,
        sectionIds,
      }),
    []
  );

  const renderRow = useCallback(
    (item: WebSection, isHalf: boolean, isPreview?: boolean) =>
      createElement(DashboardSectionRow, {
        key: item.section_id,
        id: item.section_id,
        isHalf,
        isPreview,
      }),
    []
  );

  return { announcements, renderHalfRow, renderRow };
};

export default useDashboardCustomizationRows;
